import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Breadcrumb,
  Spin,
  Timeline,
  Tabs,
  Divider,
  Row,
  Col,
  Tooltip,
  Alert,
  Button,
} from 'antd';
import {
  TrophyOutlined,
  GiftOutlined,
  UnlockOutlined,
  ThunderboltOutlined,
  StarOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  RightOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { gamificationApi } from '../../api';
import {
  RANK_SYSTEM,
  RankIcon,
  ExpProgress,
  getRankDisplayName,
} from './RankComponents';

const { Title, Paragraph, Text } = Typography;

/**
 * ランクシステム詳細ページ
 */
const RankSystemPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [expHistory, setExpHistory] = useState([]);
  const [features, setFeatures] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusData, historyData, featuresData] = await Promise.all([
        gamificationApi.getStatus(),
        gamificationApi.getExpHistory(20),
        gamificationApi.getFeaturesStatus(),
      ]);
      setStatus(statusData);
      setExpHistory(historyData);
      setFeatures(featuresData.features || {});
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  // ランクシステムテーブル用データ
  const rankTableData = RANK_SYSTEM.map(rank => {
    const isCurrentRank = status && status.current_rank === rank.rank;
    const isPassed = status && status.total_exp >= rank.min_exp;

    const bonusCredits = rank.rewards
      .filter(r => r.type === 'bonus_credit')
      .reduce((sum, r) => sum + r.amount, 0);

    // ランクゲート機能（紹介・ファクトチェックのみ）
    const featureUnlocks = rank.rewards
      .filter(r => r.type === 'feature_unlock')
      .map(r => {
        const featureInfo = {
          'referral_system': {
            name: '友達紹介機能',
            description: '友達を招待してボーナスクレジットを獲得できます',
          },
          'fact_check_mode': {
            name: '???',
            description: '',
          },
        };
        if (r.description) {
          return {
            name: r.description,
            description: featureInfo[r.feature]?.description || '',
          };
        }
        return featureInfo[r.feature] || { name: r.feature, description: '' };
      });

    const discounts = rank.rewards.filter(
      r => r.type === 'discount' || r.type === 'permanent_discount',
    );

    return {
      key: rank.rank,
      rank: rank.rank,
      name: rank.display_name,
      minExp: rank.min_exp,
      color: rank.color,
      bonusCredits,
      featureUnlocks,
      discounts,
      isCurrentRank,
      isPassed,
    };
  });

  const columns = [
    {
      title: 'ランク',
      dataIndex: 'rank',
      key: 'rank',
      width: 240,
      render: (rank, record) => (
        <Space>
          <RankIcon rank={rank} size={24} />
          <Text
            strong={record.isCurrentRank}
            style={{
              color: record.isCurrentRank ? '#1890ff' : undefined,
              fontSize: record.isCurrentRank ? 16 : 14,
              fontWeight: record.isCurrentRank ? 600 : 400,
            }}
          >
            {record.name}
          </Text>
          {record.isCurrentRank && (
            <Tag color="blue" style={{ fontWeight: 600 }}>
              現在
            </Tag>
          )}
          {record.isPassed && !record.isCurrentRank && (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
          )}
        </Space>
      ),
    },
    {
      title: '必要EXP',
      dataIndex: 'minExp',
      key: 'minExp',
      width: 150,
      align: 'right',
      render: (exp, record) => {
        const currentExp = status?.total_exp || 0;
        const isUnlocked = currentExp >= exp;

        return (
          <Space direction="vertical" size={0} align="end">
            <Space size={4}>
              {isUnlocked && (
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              )}
              <Text
                strong={record.isCurrentRank}
                style={{
                  fontSize: record.isCurrentRank ? 16 : 14,
                  color: isUnlocked ? '#52c41a' : undefined,
                }}
              >
                {exp}
              </Text>
            </Space>
            {record.isCurrentRank && status && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                (現在: {currentExp})
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'ボーナスクレジット特典',
      dataIndex: 'bonusCredits',
      key: 'bonusCredits',
      width: 200,
      align: 'center',
      render: (credits, record) => {
        const isUnlocked = record.isPassed;
        // 被紹介者の場合、bronze_3で紹介特典200クレジットを表示
        const isReferredUser = status?.is_referred || status?.referred_by;
        const isBronze3 = record.rank === 'bronze_3';
        const referralBonus = isReferredUser && isBronze3 ? 200 : 0;

        if (credits === 0 && referralBonus === 0) {
          return <Text type="secondary">-</Text>;
        }

        return (
          <Space direction="vertical" size={4} align="center">
            {credits > 0 && (
              <Tag
                color={isUnlocked ? 'green' : 'purple'}
                icon={isUnlocked ? <CheckCircleOutlined /> : <GiftOutlined />}
                style={{
                  fontSize: 13,
                  padding: '2px 8px',
                  opacity: isUnlocked && !record.isCurrentRank ? 0.7 : 1,
                }}
              >
                +{credits}
                {isUnlocked && ' (獲得済み)'}
              </Tag>
            )}
            {referralBonus > 0 && (
              <Tag
                color={isUnlocked ? 'gold' : 'orange'}
                icon={<GiftOutlined />}
                style={{
                  fontSize: 13,
                  padding: '4px 10px',
                  fontWeight: 600,
                  border: '2px solid',
                  borderColor: isUnlocked ? '#faad14' : '#fa8c16',
                  opacity: isUnlocked && !record.isCurrentRank ? 0.8 : 1,
                }}
              >
                紹介特典 +{referralBonus}
                {isUnlocked && ' (獲得済み)'}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '特典',
      dataIndex: 'featureUnlocks',
      key: 'featureUnlocks',
      render: (features, record) => {
        const isUnlocked = record.isPassed;

        return (
          <Space direction="vertical" size="small">
            {features.length === 0 &&
              record.discounts.length === 0 && <Text type="secondary">-</Text>}
            {features.map((feature, idx) => (
              <Tooltip key={idx} title={feature.description} placement="left">
                <Tag
                  color={isUnlocked ? 'green' : 'cyan'}
                  icon={
                    isUnlocked ? <CheckCircleOutlined /> : <UnlockOutlined />
                  }
                  style={{
                    cursor: 'help',
                    fontSize: 12,
                    opacity: isUnlocked && !record.isCurrentRank ? 0.7 : 1,
                  }}
                >
                  {feature.name}
                  {isUnlocked && ' ✓'}
                  {feature.description && (
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, fontSize: 12, opacity: 0.7 }}
                    />
                  )}
                </Tag>
              </Tooltip>
            ))}
            {record.discounts.map((reward, idx) => {
              const isUnlocked = record.isPassed;
              return (
                <Tooltip
                  key={`discount-${idx}`}
                  title="全プランに適用される割引"
                  placement="left"
                >
                  <Tag
                    color={isUnlocked ? 'green' : 'cyan'}
                    icon={
                      isUnlocked ? <CheckCircleOutlined /> : <UnlockOutlined />
                    }
                    style={{
                      cursor: 'help',
                      fontSize: 12,
                      opacity: isUnlocked && !record.isCurrentRank ? 0.7 : 1,
                    }}
                  >
                    {reward.description}
                    {isUnlocked && ' ✓'}
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, fontSize: 12, opacity: 0.7 }}
                    />
                  </Tag>
                </Tooltip>
              );
            })}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="aio-dashboard">
      {/* パンくずリスト */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: <Link to="/mypage">マイページ</Link>,
          },
          {
            title: 'ランクシステム',
          },
        ]}
      />

      {/* ページヘッダー */}
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title
              level={1}
              className="gradient-text"
              style={{ fontSize: 24, marginBottom: 8 }}
            >
              ランクシステム
            </Title>
            <Typography.Text type="secondary">
              アクションを実行してEXPを獲得し、ランクを上げて様々な特典をアンロックしましょう
            </Typography.Text>
          </Col>
        </Row>
      </div>

      {/* 現在のステータス */}
      {status && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {/* 現在の進捗 */}
          <Col xs={24} lg={12}>
            <Card style={{ height: '100%' }}>
              <Title level={4} style={{ marginBottom: 16 }}>
                現在の進捗
              </Title>
              <ExpProgress totalExp={status.total_exp} size="large" />
              <Divider style={{ margin: '16px 0' }} />
              <Row gutter={[16, 16]}>
                <Col span={6} style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    総EXP
                  </Text>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 600,
                      color: '#1890ff',
                      marginTop: 4,
                    }}
                  >
                    {status.total_exp}
                  </div>
                </Col>
                <Col span={6} style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ログイン日数
                  </Text>
                  <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>
                    {status.daily_login_count}
                  </div>
                </Col>
                <Col span={6} style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    リサーチ数
                  </Text>
                  <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>
                    {status.research_count || 0}
                  </div>
                </Col>
                <Col span={6} style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    記事生成数
                  </Text>
                  <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>
                    {status.generation_count}
                  </div>
                </Col>
              </Row>

              {/* 推奨アクション（チュートリアル未完了時のみ） */}
              {!status.tutorial_completed && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                      border: '1px solid #fde68a',
                      borderRadius: 8,
                      padding: '12px 16px',
                    }}
                  >
                    <Space
                      direction="vertical"
                      style={{ width: '100%' }}
                      size={8}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <BulbOutlined
                          style={{ fontSize: 16, color: '#d97706' }}
                        />
                        <Text strong style={{ fontSize: 14, color: '#92400e' }}>
                          {status.research_count === 0
                            ? '次のアクション'
                            : 'もう少しでチュートリアル完了！'}
                        </Text>
                      </div>
                      {status.research_count === 0 ? (
                        <Space
                          direction="vertical"
                          style={{ width: '100%' }}
                          size={8}
                        >
                          <Text style={{ color: '#78350f', fontSize: 13 }}>
                            まずは
                            <Text strong style={{ color: '#92400e' }}>
                              新規リサーチ
                            </Text>
                            をしてみましょう
                          </Text>
                          <Text style={{ color: '#78350f', fontSize: 12 }}>
                            SEO/AIOに強いコンテンツづくりは、検索者や競合サイトの深い理解が最初の一歩。5分程度でAIが徹底リサーチしてくれます。
                          </Text>
                          <div style={{ textAlign: 'right' }}>
                            <Button
                              size="small"
                              icon={<RightOutlined />}
                              onClick={() => navigate('/aio/analysis/new')}
                            >
                              新規リサーチを試してみる
                            </Button>
                          </div>
                        </Space>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text style={{ color: '#78350f', fontSize: 13 }}>
                            次は
                            <Text strong style={{ color: '#92400e' }}>
                              記事生成
                            </Text>
                            をしてみましょう
                          </Text>
                          <Button
                            size="small"
                            icon={<RightOutlined />}
                            onClick={() => navigate('/aio/generation/new')}
                          >
                            記事を生成する
                          </Button>
                        </div>
                      )}
                    </Space>
                  </div>
                </>
              )}
            </Card>
          </Col>

          {/* EXP獲得方法 */}
          <Col xs={24} lg={12}>
            <Card style={{ height: '100%' }}>
              <Title level={4} style={{ marginBottom: 16 }}>
                EXP獲得方法
              </Title>
              <Space
                direction="vertical"
                style={{ width: '100%' }}
                size="small"
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                  }}
                >
                  <Text>毎日ログイン</Text>
                  <Space size={4}>
                    <Tag color="blue">+2 EXP</Tag>
                    <Tag color="purple">+3 クレジット</Tag>
                  </Space>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                  }}
                >
                  <Text>リサーチ実行</Text>
                  <Tag color="blue">+2 EXP</Tag>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                  }}
                >
                  <Text>記事生成完了</Text>
                  <Tag color="blue">+5 EXP</Tag>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                  }}
                >
                  <Text>紹介成功</Text>
                  <Tag color="gold">+15 EXP</Tag>
                </div>
              </Space>

              {/* チュートリアルボーナス */}
              {(status.research_count === 0 ||
                status.generation_count === 0) && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <Title level={5} style={{ marginBottom: 12, fontSize: 14 }}>
                    チュートリアル
                  </Title>
                  <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size="small"
                  >
                    {status.research_count === 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '6px 0',
                          alignItems: 'center',
                        }}
                      >
                        <Space>
                          <CheckCircleOutlined
                            style={{ color: '#d9d9d9', fontSize: 16 }}
                          />
                          <Text type="secondary">初回リサーチ</Text>
                        </Space>
                        <Tag color="purple">+5 EXP</Tag>
                      </div>
                    )}
                    {status.research_count > 0 &&
                      status.generation_count === 0 && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 0',
                            alignItems: 'center',
                          }}
                        >
                          <Space>
                            <CheckCircleOutlined
                              style={{ color: '#52c41a', fontSize: 16 }}
                            />
                            <Text delete>初回リサーチ</Text>
                          </Space>
                          <Tag color="default">+5 EXP</Tag>
                        </div>
                      )}
                    {status.generation_count === 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '6px 0',
                          alignItems: 'center',
                        }}
                      >
                        <Space>
                          <CheckCircleOutlined
                            style={{ color: '#d9d9d9', fontSize: 16 }}
                          />
                          <Text type="secondary">初回記事生成</Text>
                        </Space>
                        <Tag color="purple">+12 EXP</Tag>
                      </div>
                    )}
                    {status.generation_count > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '6px 0',
                          alignItems: 'center',
                        }}
                      >
                        <Space>
                          <CheckCircleOutlined
                            style={{ color: '#52c41a', fontSize: 16 }}
                          />
                          <Text delete>初回記事生成</Text>
                        </Space>
                        <Tag color="default">+12 EXP</Tag>
                      </div>
                    )}
                  </Space>
                </>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <Tabs
        defaultActiveKey="ranks"
        items={[
          {
            key: 'ranks',
            label: 'ランク一覧',
            children: (
              <Card>
                <Table
                  dataSource={rankTableData}
                  columns={columns}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  rowClassName={record =>
                    record.isCurrentRank
                      ? 'current-rank-row'
                      : record.isPassed
                        ? 'passed-rank-row'
                        : ''
                  }
                />
              </Card>
            ),
          },
          {
            key: 'history',
            label: 'EXP履歴',
            children: (
              <Card>
                {expHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Text type="secondary">まだEXPの獲得履歴がありません</Text>
                  </div>
                ) : (
                  <Timeline
                    items={expHistory.map(log => ({
                      color: log.is_rank_up ? 'green' : 'blue',
                      dot: log.is_rank_up ? (
                        <TrophyOutlined />
                      ) : (
                        <ThunderboltOutlined />
                      ),
                      children: (
                        <div>
                          <Space>
                            <Text strong>
                              {log.description || log.action_type}
                            </Text>
                            <Tag color="blue">+{log.exp_amount} EXP</Tag>
                            {log.credit_amount > 0 && (
                              <Tag color="purple">
                                +{log.credit_amount} クレジット
                              </Tag>
                            )}
                          </Space>
                          {log.is_rank_up && (
                            <div style={{ marginTop: 8 }}>
                              <Text strong style={{ color: '#faad14' }}>
                                <TrophyOutlined style={{ marginRight: 4 }} />
                                ランクアップ！{' '}
                                {getRankDisplayName(log.rank_after)}
                              </Text>
                            </div>
                          )}
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {new Date(log.created_at).toLocaleString('ja-JP')}
                            </Text>
                          </div>
                        </div>
                      ),
                    }))}
                  />
                )}
              </Card>
            ),
          },
        ]}
      />

      <style>{`
        .current-rank-row {
          background-color: #e6f4ff !important;
          border-left: 3px solid #1890ff !important;
        }
        .current-rank-row:hover td {
          background-color: #e6f4ff !important;
        }
        .current-rank-row td {
          font-weight: 500;
        }
        .passed-rank-row {
          opacity: 0.5;
        }
        .passed-rank-row:hover {
          opacity: 0.65;
        }
      `}</style>
    </div>
  );
};

export default RankSystemPage;
